/**
 * Modal de configuração para node de Formatar Array
 * Permite transformar um array em texto formatado usando template
 * Suporte opcional para enviar documentos (PDFs) diretamente via WhatsApp
 * 
 * RELEASE: 1.0.0 - 2024-12-24 - Novo modal para configurar formatação de arrays
 * RELEASE: 2.1.0 - 2026-09-07 - Variável de saída migrada para o modal de destino.
 * RELEASE: 2.0.0 - 2024-12-25 - Adicionado suporte a envio de documentos (send_documents)
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FlowNode, FormatArrayConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle, FileText, ArrowRight, Info, FileDown, SortAsc, SortDesc } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { VariableFormatHelpDialog } from './VariableFormatHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { useAuth } from '@/hooks/useAuth';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';

// Schema de validação
const formatArraySchema = z.object({
  source_variable: z.string().min(1, 'Variável de entrada é obrigatória'),
  output_variable: z.string().min(1, 'Variável de saída é obrigatória'),
  item_template: z.string().min(1, 'Template do item é obrigatório'),
  separator: z.string().optional(),
  filter_field: z.string().optional(),
  filter_value: z.string().optional(),
  header_template: z.string().optional(),
  footer_template: z.string().optional(),
  empty_message: z.string().optional(),
  // Campos para envio de documentos
  send_documents: z.boolean().optional(),
  document_url_field: z.string().optional(),
  document_caption_template: z.string().optional(),
  document_filename_template: z.string().optional(),
  // NOVOS: Campos para limite e ordenação
  limit_items: z.number().min(0).optional(),
  sort_field: z.string().optional(),
  sort_direction: z.enum(['asc', 'desc']).optional(),
});

interface ConfigureFormatArrayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: FormatArrayConfig) => void;
}

/**
 * Dialog de configuração de Formatar Array
 */
export function ConfigureFormatArrayDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave 
}: ConfigureFormatArrayDialogProps) {
  const { currentTenant } = useAuth();
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estados do formulário
  const [sourceVariable, setSourceVariable] = useState(node?.data.config?.source_variable || '');
  const [outputVariable, setOutputVariable] = useState(node?.data.config?.output_variable || '');
  const [itemTemplate, setItemTemplate] = useState(node?.data.config?.item_template || '');
  const [separator, setSeparator] = useState(node?.data.config?.separator || '\n\n');
  const [filterField, setFilterField] = useState(node?.data.config?.filter_field || '');
  const [filterValue, setFilterValue] = useState(node?.data.config?.filter_value || '');
  const [headerTemplate, setHeaderTemplate] = useState(node?.data.config?.header_template || '');
  const [footerTemplate, setFooterTemplate] = useState(node?.data.config?.footer_template || '');
  const [emptyMessage, setEmptyMessage] = useState(node?.data.config?.empty_message || 'Nenhum item encontrado.');
  const [showHelp, setShowHelp] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFormatHelp, setShowFormatHelp] = useState(false);
  
  // Estados para envio de documentos
  const [sendDocuments, setSendDocuments] = useState(node?.data.config?.send_documents || false);
  const [documentUrlField, setDocumentUrlField] = useState(node?.data.config?.document_url_field || '');
  const [documentCaptionTemplate, setDocumentCaptionTemplate] = useState(node?.data.config?.document_caption_template || '');
  const [documentFilenameTemplate, setDocumentFilenameTemplate] = useState(node?.data.config?.document_filename_template || 'documento_{{$index}}.pdf');
  
  // NOVOS: Estados para limite e ordenação
  const [limitItems, setLimitItems] = useState<number>(node?.data.config?.limit_items || 0);
  const [sortField, setSortField] = useState(node?.data.config?.sort_field || '');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(node?.data.config?.sort_direction || 'asc');

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      setSourceVariable(node.data.config?.source_variable || '');
      setOutputVariable(node.data.config?.output_variable || '');
      setItemTemplate(node.data.config?.item_template || '');
      setSeparator(node.data.config?.separator || '\n\n');
      setFilterField(node.data.config?.filter_field || '');
      setFilterValue(node.data.config?.filter_value || '');
      setHeaderTemplate(node.data.config?.header_template || '');
      setFooterTemplate(node.data.config?.footer_template || '');
      setEmptyMessage(node.data.config?.empty_message || 'Nenhum item encontrado.');
      
      // Estados para envio de documentos
      setSendDocuments(node.data.config?.send_documents || false);
      setDocumentUrlField(node.data.config?.document_url_field || '');
      setDocumentCaptionTemplate(node.data.config?.document_caption_template || '');
      setDocumentFilenameTemplate(node.data.config?.document_filename_template || 'documento_{{$index}}.pdf');
      
      // NOVOS: Estados para limite e ordenação
      setLimitItems(node.data.config?.limit_items || 0);
      setSortField(node.data.config?.sort_field || '');
      setSortDirection(node.data.config?.sort_direction || 'asc');
      
      // Mostra seção avançada se tiver algum campo preenchido
      if (node.data.config?.filter_field || node.data.config?.header_template || 
          node.data.config?.footer_template || node.data.config?.send_documents ||
          node.data.config?.limit_items || node.data.config?.sort_field) {
        setShowAdvanced(true);
      }
    }
  }, [node]);

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    // Valida campos de documentos se o modo estiver ativo
    if (sendDocuments && !documentUrlField) {
      toast.error('Campo de URL do documento é obrigatório quando envio de documentos está ativo');
      return;
    }

    try {
      const validatedData = formatArraySchema.parse({
        source_variable: sourceVariable,
        output_variable: outputVariable,
        item_template: itemTemplate,
        separator: separator || '\n\n',
        filter_field: filterField || undefined,
        filter_value: filterValue || undefined,
        header_template: headerTemplate || undefined,
        footer_template: footerTemplate || undefined,
        empty_message: emptyMessage || 'Nenhum item encontrado.',
        // Campos de documentos
        send_documents: sendDocuments,
        document_url_field: documentUrlField || undefined,
        document_caption_template: documentCaptionTemplate || undefined,
        document_filename_template: documentFilenameTemplate || 'documento_{{$index}}.pdf',
        // NOVOS: Campos de limite e ordenação
        limit_items: limitItems > 0 ? limitItems : undefined,
        sort_field: sortField || undefined,
        sort_direction: sortField ? sortDirection : undefined,
      });

      const config: FormatArrayConfig = {
        source_variable: validatedData.source_variable,
        output_variable: validatedData.output_variable,
        item_template: validatedData.item_template,
        separator: validatedData.separator,
        filter_field: validatedData.filter_field,
        filter_value: validatedData.filter_value,
        header_template: validatedData.header_template,
        footer_template: validatedData.footer_template,
        empty_message: validatedData.empty_message,
        // Campos de documentos
        send_documents: validatedData.send_documents,
        document_url_field: validatedData.document_url_field,
        document_caption_template: validatedData.document_caption_template,
        document_filename_template: validatedData.document_filename_template,
        // NOVOS: Campos de limite e ordenação
        limit_items: validatedData.limit_items,
        sort_field: validatedData.sort_field,
        sort_direction: validatedData.sort_direction,
      };

      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-500" />
                Configurar Formatar Array
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
              {/* Campo ID do Node (somente leitura) */}
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* Variável de Entrada */}
              <div className="space-y-2">
                <Label>Variável de Entrada (Array) *</Label>
                <VariablePickerCombobox
                  tenantId={currentTenant?.id || ''}
                  value={sourceVariable}
                  onChange={(variable) => {
                    setSourceVariable(variable?.name || '');
                  }}
                  allowClear={true}
                  placeholder="Selecione a variável de entrada"
                />
                <p className="text-xs text-muted-foreground">
                  Variável que contém o array a ser formatado (ex: boletos_abertos)
                </p>
              </div>

              {/* Template do Item */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  Template do Item *
                  <HelpCircle
                    className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                    onClick={() => setShowFormatHelp(true)}
                  />
                </Label>
                <Textarea
                  placeholder={`📄 *Boleto {{$index}}*\nCódigo: {{codigo_barras}}\nLink: {{link_boleto}}\nValor: R$ {{valor}}`}
                  value={itemTemplate}
                  onChange={(e) => setItemTemplate(e.target.value)}
                  className="min-h-[100px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Use <code className="bg-muted px-1 rounded">{`{{campo}}`}</code> para campos do item e{' '}
                  <code className="bg-muted px-1 rounded">{`{{$index}}`}</code> para o número da linha
                </p>
              </div>

              {/* Separador */}
              <div className="space-y-2">
                <Label>Separador entre Itens</Label>
                <Input
                  placeholder="\n\n"
                  value={separator}
                  onChange={(e) => setSeparator(e.target.value)}
                  maxLength={50}
                />
                <p className="text-xs text-muted-foreground">
                  Texto entre cada item (use \n para nova linha). Padrão: duas linhas em branco
                </p>
              </div>

              {/* Variável de Saída */}
              <div className="space-y-2">
                <Label>Variável de Saída *</Label>
                <VariableDestinationPicker
                  tenantId={currentTenant?.id || ''}
                  value={outputVariable}
                  valueName={outputVariable}
                  onChange={(variable) => {
                    setOutputVariable(variable?.name || '');
                  }}
                  allowClear
                  placeholder="Selecione ou crie a variável de saída"
                />
                <p className="text-xs text-muted-foreground">
                  Variável onde o texto formatado será salvo (use em Message Text: {`{{lista_formatada}}`})
                </p>
              </div>

              {/* Seção avançada */}
              <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between">
                    <span>Opções Avançadas</span>
                    <span className="text-xs text-muted-foreground">
                      {showAdvanced ? '▲' : '▼'}
                    </span>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  {/* Filtro adicional */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Filtrar por Campo</Label>
                      <Input
                        placeholder="veiculo_modelo"
                        value={filterField}
                        onChange={(e) => setFilterField(e.target.value)}
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Valor do Filtro</Label>
                      <Input
                        placeholder="{{veiculo_selecionado}}"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        maxLength={200}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Filtra os itens antes de formatar (ex: mostrar só boletos do veículo selecionado)
                  </p>

                  {/* Header/Footer */}
                  <div className="space-y-2">
                    <Label>Texto de Cabeçalho</Label>
                    <Input
                      placeholder="📋 Seus boletos do veículo {{veiculo_selecionado}}:"
                      value={headerTemplate}
                      onChange={(e) => setHeaderTemplate(e.target.value)}
                      maxLength={500}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Texto de Rodapé</Label>
                    <Input
                      placeholder="Total: {{$count}} boleto(s)"
                      value={footerTemplate}
                      onChange={(e) => setFooterTemplate(e.target.value)}
                      maxLength={500}
                    />
                  </div>

                  {/* Mensagem se vazio */}
                  <div className="space-y-2">
                    <Label>Mensagem se Array Vazio</Label>
                    <Input
                      placeholder="Nenhum item encontrado."
                      value={emptyMessage}
                      onChange={(e) => setEmptyMessage(e.target.value)}
                      maxLength={200}
                    />
                  </div>
                  
                  {/* NOVO: Seção de Limite e Ordenação */}
                  <div className="border-t border-border pt-4 mt-4">
                    <div className="flex items-center gap-2 mb-4">
                      {sortDirection === 'asc' ? (
                        <SortAsc className="h-4 w-4 text-orange-500" />
                      ) : (
                        <SortDesc className="h-4 w-4 text-orange-500" />
                      )}
                      <Label className="font-medium">Limite e Ordenação</Label>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Limite de itens */}
                      <div className="space-y-2">
                        <Label>Limite de Itens</Label>
                        <Input
                          type="number"
                          placeholder="0 (todos)"
                          value={limitItems || ''}
                          onChange={(e) => setLimitItems(parseInt(e.target.value) || 0)}
                          min={0}
                          max={100}
                        />
                        <p className="text-xs text-muted-foreground">
                          Quantidade máxima de itens a processar. 0 = todos os itens.
                        </p>
                      </div>
                      
                      {/* Ordenação */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Ordenar por Campo</Label>
                          <Input
                            placeholder="vencimento"
                            value={sortField}
                            onChange={(e) => setSortField(e.target.value)}
                            maxLength={100}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Direção</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={sortDirection === 'asc' ? 'default' : 'outline'}
                              size="sm"
                              className="flex-1"
                              onClick={() => setSortDirection('asc')}
                            >
                              <SortAsc className="h-4 w-4 mr-1" />
                              Crescente
                            </Button>
                            <Button
                              type="button"
                              variant={sortDirection === 'desc' ? 'default' : 'outline'}
                              size="sm"
                              className="flex-1"
                              onClick={() => setSortDirection('desc')}
                            >
                              <SortDesc className="h-4 w-4 mr-1" />
                              Decrescente
                            </Button>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Ex: ordenar por "vencimento" crescente mostra boletos mais próximos primeiro.
                      </p>
                    </div>
                  </div>
                  
                  {/* Seção de Envio de Documentos */}
                  <div className="border-t border-border pt-4 mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <FileDown className="h-4 w-4 text-blue-500" />
                        <Label className="font-medium">Enviar Documentos em vez de Links</Label>
                      </div>
                      <Switch
                        checked={sendDocuments}
                        onCheckedChange={setSendDocuments}
                      />
                    </div>
                    
                    {sendDocuments && (
                      <div className="space-y-4 pl-6 border-l-2 border-blue-500/30">
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-4">
                          <div className="flex gap-2">
                            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-700 dark:text-blue-400">
                              <p>Quando ativo, cada item do array com URL será enviado como arquivo PDF via WhatsApp.</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Campo com URL do Documento *</Label>
                          <Input
                            placeholder="boleto_link"
                            value={documentUrlField}
                            onChange={(e) => setDocumentUrlField(e.target.value)}
                            maxLength={100}
                          />
                          <p className="text-xs text-muted-foreground">
                            Nome do campo no array que contém a URL do arquivo (ex: boleto_link, documento_url)
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1.5">
                            Template da Legenda
                            <HelpCircle
                              className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                              onClick={() => setShowFormatHelp(true)}
                            />
                          </Label>
                          <Input
                            placeholder="📄 Boleto {{$index}} - Vencimento: {{vencimento}} - R$ {{valor}}"
                            value={documentCaptionTemplate}
                            onChange={(e) => setDocumentCaptionTemplate(e.target.value)}
                            maxLength={500}
                          />
                          <p className="text-xs text-muted-foreground">
                            Texto exibido junto ao documento. Use {`{{campo}}`} para valores do item.
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Template do Nome do Arquivo</Label>
                          <Input
                            placeholder="boleto_{{$index}}.pdf"
                            value={documentFilenameTemplate}
                            onChange={(e) => setDocumentFilenameTemplate(e.target.value)}
                            maxLength={100}
                          />
                          <p className="text-xs text-muted-foreground">
                            Nome do arquivo enviado. Use {`{{$index}}`} para número, {`{{campo}}`} para valores.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Preview visual */}
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <p className="text-sm font-medium mb-3">Pré-visualização:</p>
                <div className="flex items-center gap-2 text-sm font-mono flex-wrap">
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 rounded">
                    {sourceVariable || 'array_entrada'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">formatar</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <span className="px-2 py-1 bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 rounded">
                    {outputVariable || 'texto_saida'}
                  </span>
                </div>
              </div>

              {/* Dica de uso */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <div className="flex gap-2">
                  <Info className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-emerald-700 dark:text-emerald-400">
                    <p className="font-medium mb-1">💡 Exemplo de uso:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Webhook retorna array de boletos em <code>boletos_abertos</code></li>
                      <li>Question List filtra por veículo → <code>veiculo_selecionado</code></li>
                      <li>Format Array formata boletos em <code>lista_formatada</code></li>
                      <li>Message Text exibe: <code>{`{{lista_formatada}}`}</code></li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="format_array" 
      />

      {/* Modal de ajuda sobre formatos de variáveis */}
      <VariableFormatHelpDialog
        open={showFormatHelp}
        onOpenChange={setShowFormatHelp}
      />
    </>
  );
}
